import { Environment, ExternalPackageInfoType } from 'src/sdkInfoTypes';
import {
  UrlFirstParameter,
  UrlParameter,
  UrlTracking,
  UrlQuery,
  DEFAULT_DOMAINS,
} from './constants';
import {
  DataApiQuery,
  DomainsType,
  GetVisitorDataUrlParamsType,
  UrlProviderInitializeParamsType,
  UrlType,
} from './types';

export interface IUrlProvider {
  initialize(params: UrlProviderInitializeParamsType): void;
  getClientConfigurationUrl(timeStamp?: number): string;
  getEventSourceUrl(): string;
  getRemoteDataUrl(key: string): string;
  getVisitorDataUrl(params: GetVisitorDataUrlParamsType): string;
  getTrackingUrl(isBodyUserAgent: boolean): string;
  dataApiDomain: string;
}

export class UrlProvider implements IUrlProvider {
  private ready = false;
  private isCustomDomain = false;
  private domains: DomainsType = DEFAULT_DOMAINS;
  private siteCode?: string;
  private environment?: string;
  private packageInfo?: ExternalPackageInfoType;

  public initialize({
    domain,
    siteCode,
    packageInfo,
    environment,
  }: UrlProviderInitializeParamsType) {
    this.siteCode = siteCode;
    this.environment = environment;
    this.packageInfo = packageInfo;
    this.ready = true;

    this.setDomains(domain);
  }

  set dataApiDomain(domain: string) {
    if (!this.isCustomDomain) {
      this.domains[UrlType.DataApi] = domain;
      return;
    }

    const subDomain = domain.split('.')[0];
    const currentDomain = this.domains[UrlType.DataApi];
    this.domains[UrlType.DataApi] = currentDomain.replace(/^[^.]+/, subDomain);
  }

  get dataApiDomain(): string {
    return this.domains[UrlType.DataApi];
  }

  public getClientConfigurationUrl(timeStamp?: number): string {
    this.isInitialized();

    const baseUrl = `https://${this.domains[UrlType.ClientConfiguration]}/v3/`;
    const environmentParam =
      this.environment === Environment.Production
        ? ''
        : UrlFirstParameter.Environment + encodeURIComponent(this.environment!);
    let timeStampParam = '';

    if (timeStamp) {
      if (environmentParam) {
        timeStampParam = UrlParameter.Ts + timeStamp;
      } else {
        timeStampParam = UrlFirstParameter.Ts + timeStamp;
      }
    }

    return baseUrl + this.siteCode + environmentParam + timeStampParam;
  }

  public getEventSourceUrl(): string {
    this.isInitialized();

    return `https://${this.domains[UrlType.Events]}:8110/${UrlQuery.Sse}${
      this.siteCode
    }`;
  }

  public getRemoteDataUrl(key: string): string {
    this.isInitialized();

    return (
      this.getDataApiUrl(DataApiQuery.DataMap) +
      this.siteCode +
      UrlParameter.Key +
      encodeURI(key)
    );
  }

  public getVisitorDataUrl({
    visitorCode,
    filters,
    isMappingIdentifier,
  }: GetVisitorDataUrlParamsType): string {
    this.isInitialized();

    const {
      customData,
      previousVisitAmount,
      currentVisit,
      conversions,
      geolocation,
      experiments,
      pageViews,
      device,
      browser,
      operatingSystem,
      kcs,
      personalization,
      visitorCode: visitorCodeFilter,
      cbs,
    } = filters;

    const identifierParameter = isMappingIdentifier
      ? UrlParameter.MappingValue
      : UrlParameter.VisitorCode;
    const customDataParameter =
      customData || visitorCodeFilter ? UrlParameter.CustomData + true : '';
    const conversionsParameter = conversions
      ? UrlParameter.Conversion + true
      : '';
    const geolocationParameter = geolocation
      ? UrlParameter.Geolocation + true
      : '';
    const experimentsParameter = experiments
      ? UrlParameter.Experiment + true
      : '';
    const pageViewsParameter = pageViews ? UrlParameter.Page + true : '';
    const staticDataParameter = UrlParameter.StaticData + true;
    const currentVisitParameter = currentVisit
      ? UrlParameter.CurrentVisit + true
      : '';
    const kcsParameter = kcs ? UrlParameter.Kcs + true : '';
    const personalizationParameter = personalization
      ? UrlParameter.Personalization + true
      : '';
    const previousVisitAmountParam =
      typeof previousVisitAmount !== 'number'
        ? UrlParameter.MaxNumberPreviousVisits + 1
        : UrlParameter.MaxNumberPreviousVisits + previousVisitAmount;
    const cbsParameter = cbs ? UrlParameter.Cbs + true : '';

    return (
      this.getDataApiUrl(DataApiQuery.VisitData) +
      this.siteCode +
      identifierParameter +
      visitorCode +
      previousVisitAmountParam +
      customDataParameter +
      conversionsParameter +
      geolocationParameter +
      experimentsParameter +
      pageViewsParameter +
      staticDataParameter +
      currentVisitParameter +
      kcsParameter +
      personalizationParameter +
      cbsParameter
    );
  }

  public getTrackingUrl(isBodyUserAgent: boolean): string {
    this.isInitialized();

    const { type, version } = this.packageInfo!;

    let url =
      this.getDataApiUrl(DataApiQuery.VisitEvent) +
      this.siteCode +
      UrlParameter.SdkName +
      type.toLowerCase() +
      UrlParameter.SdkVersion +
      version;

    if (isBodyUserAgent) {
      url += UrlParameter.BodyUa + true;
    }

    return url;
  }

  private isInitialized(): void {
    if (!this.ready) {
      throw new Error('UrlProvider is not initialized');
    }
  }

  private getDataApiUrl(query: DataApiQuery): string {
    if (!this.domains[UrlType.DataApi]) {
      throw new Error('Data API domain is not set');
    }

    const domain = `https://${this.domains[UrlType.DataApi]}`;

    switch (query) {
      case DataApiQuery.VisitEvent:
        return `${domain}/${UrlTracking.Visit + UrlQuery.Events}`;
      case DataApiQuery.VisitData:
        return `${domain}/${UrlTracking.Visit + UrlQuery.Visitor}`;
      case DataApiQuery.DataMap:
        return `${domain}/${UrlTracking.Map + UrlQuery.Map}`;
    }
  }

  private setDomains(domain: string | null): void {
    if (!domain) {
      return;
    }

    this.isCustomDomain = true;

    this.domains[UrlType.DataApi] = 'data.' + domain;
    this.domains[UrlType.Events] = 'events.' + domain;
    this.domains[UrlType.ClientConfiguration] = 'sdk-config.' + domain;
  }
}
